// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useState } from 'react';
import Thread from './Thread';
import { EXPAND_MEMO_STEP } from 'memos/MemoConstants';
import useStreamHandler from 'utils/useStreamHandler';
import { useHasAICoding } from 'hooks/feature-flags/queries';
import { useCurrentUserWithInitials } from 'hooks/users/useCurrentUserWithInitials';
import { useCreateMemoChat } from 'hooks/memos/mutations';
import { useSortedMemos } from 'hooks/memos/useSortedMemos';
import { useMemoCutoffTime } from 'hooks/memos/useMemoCutoffTime';
import { useFilteredMemoDisplay } from 'hooks/memos/useFilteredMemoDisplay';


function ThreadContainer(props) {
  const [streamConfig, setStreamConfig] = useState({ url: null, headers: null });
   const { data: streamData } = useStreamHandler(streamConfig.url, streamConfig.headers);
  const { user } = useCurrentUserWithInitials();
  const { hasAICoding: featureFlagAICoding } = useHasAICoding();
  const createMemoChatMutation = useCreateMemoChat();
  
  // Get sorted memos with streaming updates applied
  const allMemos = useSortedMemos(props.memos, streamConfig, streamData);
  
  // Manage cutoff time for unexpanded state
  const cutoffTime = useMemoCutoffTime(props.memosExpanded, allMemos);

  async function askAI() {
    if (!featureFlagAICoding) return;
    if (!process.env['AI_FEATURE_FLAG']) return;
    
    try {
      const result = await createMemoChatMutation.mutateAsync({
        excerptId: props.excerpt_id
      });
      
      // Expand the memo and set up streaming
      props.memoExpanded(result.memo.id);
      setStreamConfig({
        url: result.stream.stream_url,
        headers: result.auth,
        memo_id: result.memo.id
      });
    } catch (error) {
      console.error('Error creating memo chat:', error);
    }
  }
  
  // Filter memos based on expanded state and cutoff time
  const memoCount = props.memoCount;
  const memos = useFilteredMemoDisplay(allMemos, props.memosExpanded, memoCount, cutoffTime);
  
  // Early return after all hooks have been called
  if (!props.memosExpanded && allMemos.length === 0) return null;
  
  const memosToSee = Math.min(allMemos.length - memos.length, EXPAND_MEMO_STEP);

  return (
    <Thread
      memosToSee={memosToSee}
      memos={memos}
      hasEditPermission={props.hasEditPermission}
      initials={user?.initials}
      userColor={user?.color}
      excerpt_id={props.excerpt_id}
      memoExpanded={props.memoExpanded}
      expandedMemoList={props.expandedMemoList}
      increaseMemoCount={props.increaseMemoCount}
      askAI={askAI}
      featureFlagAICoding={featureFlagAICoding}
      userId={user?.id}
    />
  );
}

export default ThreadContainer;